/* eslint-disable no-prototype-builtins */
/**
 * Helper services to support product manipulation.
 * Methods not directly related to the get and set of behaviour subjects or fetching of api data.
 */
import { Injectable } from '@angular/core';
import { IWhenAttending, TCalendarAvailability } from '@app/when-attending/when-attending.model';
import { ISessionDiscount } from '@core/product/models/product-session-discount.model';
import { AppConfigService } from '../app-config/app-config.service';
import { DateFormatService } from '../date/date-format.service';
import { ICheckoutRecurringPayment } from './models/checkout-recurring-payment.model';
import { CEmptyMinProductCost, IMinProductCost } from './models/min-product-cost.model';
import { IProductAllocation } from './models/product-allocation.model';
import { IProductInfo } from './models/product-info.model';
import { IProductMeta } from './models/product-meta.model';
import { IProductPackageItem } from './models/product-package-item.model';
import { IProductSession, TWhenAttendingProductSession } from './models/product-session.model';
import { CCannotBeAddedToExistingBooking, TProductType } from './models/product-type.type';
import { EProductSubTypes, EProductTypes, IProduct } from './models/product.model';
import { sessionHasCapacity } from '../availability-calculation/availability-calculation.util';
import { getEarlyBirdPricingRuleBracket, getTicketTier } from '@roller/booking-calculation-service';
import { ProductListService } from '@core/product/product-list.service';

@Injectable({
  providedIn: 'root'
})
export class ProductHelperService {
  private readonly productTypes = EProductTypes;
  private readonly productSubTypes = EProductSubTypes;

  constructor(
    private appConfigService: AppConfigService,
    private dateFormatService: DateFormatService,
    private productListService: ProductListService
  ) {}

  /**
   * Refreshes if the data is more than an hour old.
   * @param lastQuery the date the query was made.
   * @returns true if the query is under an hour.
   */
  isQueryFresh(lastQuery: Date): boolean {
    const nowDate = new Date().getTime();
    const timeDiff = nowDate - lastQuery.getTime();
    const timeToRefresh: number =
      this.appConfigService.getAppValue<number | undefined>('refreshStoredQueriesAfter') || 600000;
    return timeDiff < timeToRefresh;
  }

  /** Returns an array of calculated session lengths for a given product */
  getProductSessionLengths(product: IProduct): (string | null)[] {
    return [
      ...new Set(
        product.sessions?.map((session: IProductSession) =>
          this.calculateSessionLength(session.startTime, session.endTime)
        )
      )
    ];
  }

  /** Given strings of start and end times calculates the session length in min or hours as appropriate. */
  calculateSessionLength(startTime: string, endTime?: string, minutesOnly = false): string | null {
    if (!endTime) {
      return null;
    }

    //Convert string times to numbers to make comparison safer. Only covers any given 24 hour time span
    const endTimeNextDay =
      this.dateFormatService.hourStringToNumber(startTime) > this.dateFormatService.hourStringToNumber(endTime) ||
      endTime === '00:00';

    let sessionLength = '';
    const fakeDate = (time: string, isNextDay = false) =>
      isNextDay ? `2001-10-11T${time}:00` : `2001-10-10T${time}:00`;

    const startDate = new Date(fakeDate(startTime)).getTime();
    const endDate = new Date(fakeDate(endTime, endTimeNextDay)).getTime();
    let delta = (endDate - startDate) / 1000;
    if (isNaN(delta) || delta <= 0) {
      return null;
    }

    const hours = Math.floor(delta / 3600) % 24;
    if (hours > 1 && !minutesOnly) {
      sessionLength += `${hours} HRS `;
      delta -= hours * 3600;
    }

    const minutes = Math.floor(delta / 60);
    if (minutes > 0) {
      sessionLength += `${minutes} MINS `;
    }

    return sessionLength.slice(0, -1);
  }

  /**
   * Filters sessions to remove ones where:
   * The session online sales are not open, OR the session does not contain at least 1 ticket with capacity
   * * The API should be setting onlineSalesOpen based on the sales cut-off set to allow cut-offs for before/after the session starts
   */
  filterOpenSalesAvailableSessions(
    tickets: IProductInfo[],
    sessions: IProductSession[] | undefined,
    isBookingEdit?: boolean,
    includeSoldOut?: boolean
  ): IProductSession[] | undefined {
    if (!tickets.length || !sessions?.length) {
      return undefined;
    }

    const minTicketsNeeded = Math.min(...tickets.map((ticket: IProductInfo) => ticket.minPurchase));

    return sessions.filter((session: IProductSession) => {
      if (!isBookingEdit && !session.onlineSalesOpen) {
        return false;
      }

      return this.isSessionAvailable(session, minTicketsNeeded, includeSoldOut);
    });
  }

  /**
   * Determines if online sales are closed based on availability. Availability must be explicitly set as 'onlineSalesClosed'.
   * Handles an array of sessions for session products, or a date list and date for date level product checks
   * @param sessions list of product sessions to check
   * @param selectedDate the current date (only requires a date object)
   * @param datesList date range containing the above selected date (IWhenAttending with availability type set)
   * @returns defaults to false, true if 'onlineSalesClosed' is set for the current availability
   */
  isOnlineSalesClosed(
    sessions: IProductSession[] | undefined,
    selectedDate?: IWhenAttending,
    datesList?: IWhenAttending[],
    isBookingEdit?: boolean
  ): boolean {
    if (isBookingEdit) {
      return false;
    }

    if (sessions?.length) {
      return sessions.every((session: IProductSession) => !session.onlineSalesOpen);
    }

    if (!!selectedDate && datesList?.length) {
      const selectedDateTypes: TCalendarAvailability[] = ['available', 'filling', 'fillingFast', 'onlineSalesClosed'];

      const selectedDateInList = datesList.find(
        (whenAt: IWhenAttending) =>
          selectedDateTypes.includes(whenAt.availability as TCalendarAvailability) &&
          this.dateFormatService.isSameDay(whenAt.date, selectedDate?.date)
      );

      return selectedDateInList?.availability === 'onlineSalesClosed';
    }

    return false;
  }

  /** Rough calculation of sold out based on capacity remaining. */
  isAllSessionSoldOut(tickets: IProductInfo[], sessions: IProductSession[] | undefined): boolean {
    if (!tickets.length || !sessions?.length) {
      return false;
    }

    const minTicketsNeeded = Math.min(...tickets.map((ticket: IProductInfo) => ticket.minPurchase));

    return !sessions.some((session: IProductSession) => {
      return this.isSessionAvailable(session, minTicketsNeeded);
    });
  }

  /** Determine if a when attending list is mostly available */
  isWhenAttendingMostlyAvailable(datesList: IWhenAttending[], minDaysUnavailable = 4): boolean {
    if (!datesList || !datesList.length) {
      return true;
    }

    const unavailableTypes: TCalendarAvailability[] = ['notAvailable', 'soldOut', 'onlineSalesClosed'];
    const unavailableDates = datesList.filter(
      (wAttend: IWhenAttending) => wAttend.availability && unavailableTypes.includes(wAttend.availability)
    );

    return unavailableDates.length < minDaysUnavailable;
  }

  /** Links a packages related products and finds any available sessions.  */
  getAvailablePackageSessions(packageVariations: IProductInfo[], availability: IProduct[]): IProduct[] {
    // Get unique package productId values from the package products array.
    const variationIdToQuantities = new Map<string, number>();
    packageVariations.forEach((packageVariation: IProductInfo) => {
      packageVariation.packageItems?.forEach((packageItem: IProductPackageItem) => {
        const variationId = packageItem.productId.toString();

        if (variationIdToQuantities.has(variationId)) {
          const currentQuantity = variationIdToQuantities.get(variationId);
          if (currentQuantity && packageItem.quantity < currentQuantity) {
            variationIdToQuantities.set(variationId, packageItem.quantity);
          }
        } else {
          variationIdToQuantities.set(variationId, packageItem.quantity);
        }
      });
    });

    // Filter out unrelated availability
    const relatedSessionProducts = availability.filter((item: IProduct) => {
      return (
        item.products.some((fProduct: IProductInfo) => variationIdToQuantities.has(fProduct.id)) &&
        !!item.sessions &&
        item.sessions.length > 0 &&
        item.sessions.some((session: IProductSession) => session.allocations && session.allocations.length)
      );
    });

    // Update capacities based on package product quantity
    return relatedSessionProducts.map((item: IProduct) => {
      const clonedItem = structuredClone(item);

      clonedItem.sessions?.forEach((session: IProductSession) => {
        clonedItem.products.forEach((productVariation: IProductInfo) => {
          const allocation = session.allocations.find(
            (allocation: IProductAllocation) => allocation.productId === productVariation.id
          );
          if (allocation?.bookableCapacityRemaining) {
            const quantity = variationIdToQuantities.get(productVariation.id) || 1;
            allocation.bookableCapacityRemaining = Math.floor(allocation?.bookableCapacityRemaining / quantity);
          }
        });

        const allocationBookableCapacities = session.allocations.map(
          (allocation: IProductAllocation) => allocation.bookableCapacityRemaining || 0
        );
        session.ticketCapacityRemaining = Math.max(...allocationBookableCapacities);
      });
      return clonedItem;
    });
  }

  /** Determine if the package is available by checking whether any of it's variations:
   *  * Have availability
   *  * And all their items have availability
   */
  isPackageAvailable(products: IProductInfo[], availability: IProduct[]): boolean {
    return products.some((variation: IProductInfo) => {
      return (
        this.hasAnyAvailability(variation.parentProductId, availability) &&
        ((!variation.packageItems && variation.packageOptionSets?.length) ||
          variation.packageItems?.every((packageItem: IProductPackageItem) => {
            return availability.some(
              (item: IProduct) =>
                item.parentProductId === packageItem.parentProductId &&
                ((item.availabilities?.length ?? 0) > 0 || (item.sessions?.length ?? 0) > 0) &&
                // Check that the individual product variation exists on the availability. It will be missing if hidden from online sales.
                item.products.some((p) => p.id == packageItem.productId)
            );
          }))
      );
    });
  }

  isSessionAvailable(session: IProductSession, minTicketsNeeded: number, includeSoldOut?: boolean): boolean {
    return session.allocations?.some((alloc: IProductAllocation) => {
      if (includeSoldOut) {
        return true;
      }

      return (
        (alloc.bookableCapacityRemaining === null || alloc.bookableCapacityRemaining >= minTicketsNeeded) &&
        (alloc.capacityRemaining === null || alloc.capacityRemaining > 0)
      );
    });
  }

  /** Determine if a product has any availability in the given list of products */
  private hasAnyAvailability(parentProductId: string | undefined, availability: IProduct[]): boolean {
    return availability.some(
      (item: IProduct) => item.parentProductId === parentProductId && (item.availabilities?.length ?? 0) > 0
    );
  }

  /**
   * Flattens the list of related available product sessions for a package and filters out 1:1 duplicates by date, time, and allocations.
   * @param products list of package products
   * @returns list of available product sessions
   */
  getPackageUniqueSessions(products: IProduct[]): IProductSession[] {
    let sessions: IProductSession[] = [];

    products.forEach((product: IProduct) => {
      if (product.sessions) {
        sessions = [...sessions, ...product.sessions];
      }
    });

    return sessions.filter((value: IProductSession, index: number, self: IProductSession[]) => {
      return (
        self.findIndex((session: IProductSession) => {
          return this.isSameSession(session, value) && this.hasSameAllocations(session, value);
        }) === index
      );
    });
  }

  /** Checks if two sessions have the same product allocations. */
  private hasSameAllocations(session: IProductSession, productSession: IProductSession): boolean {
    return (
      session.allocations.length === productSession.allocations.length &&
      session.allocations.every(
        (allocation: IProductAllocation, i: number) => allocation.productId === productSession.allocations[i].productId
      )
    );
  }

  /** Merges the product list with the partially populated availability list. */
  mergeProductsAvailabilities(availability: IProduct[], productList: IProduct[]): IProduct[] {
    return productList.map((product: IProduct) => {
      const findAvailability = availability.find((avail: IProduct) => avail.id === product.id);
      if (findAvailability) {
        return {
          ...product,
          products: findAvailability.products.map((availableProduct: IProductInfo) => {
            return {
              ...product.products.find((ticket) => ticket.id === availableProduct.id),
              ...availableProduct
            };
          }),
          availabilities: findAvailability.availabilities,
          sessions: findAvailability.sessions
        };
      }

      return { ...product };
    });
  }

  /**
   * Find the lowest product in the current product - e.g. child ticket.
   * * Return 0 if not food and beverage and giftcards, wallets, addon, custom, recurringPass (later), cashlesscard
   * * If membership upgrade then exclude products with lower cost
   * * For products with a minimum ticket purchase, the minimum total will be display
   * * If a ticket cost is 0 and there are higher values - exclude the 0
   * * If all else fails return 0
   * * If there is only one ticket don't display the From
   * * If there is a forced min purchase return the min cost of package.
   * * If there is an early bird price rule, return the min cost based on the price rule bracket for the selected date
   * * UX `product-item.component.html` - will no longer show if it is 0 (falsy)
   * @returns the lowest cost item in the product products cost along with descriptor for key to use.
   */
  minProductCost(
    product: IProduct,
    isFoodAndBev?: boolean,
    bookingDate?: Date,
    currentVenueTime?: Date
  ): IMinProductCost {
    const noFromPrice: TProductType[] = isFoodAndBev
      ? []
      : ['giftcard', 'wallet', 'addon', 'custom', 'recurringpass', 'cashlesscard'];

    if (!product.type || noFromPrice.includes(product.type)) {
      return CEmptyMinProductCost;
    }

    const minList: number[] = [];
    const productInfoList: IProductInfo[] = [];
    const priceRuleMinList: number[] = [];
    const earlyBirdPrices: number[] = [];

    // safety check for unit tests or malformed typecast as product
    if (Object.keys(product).length > 0) {
      // A product could have a number of forced min purchase tickets, therefore this adds to the base cost of every ticket
      const packageForcedCost = product.products.reduce((val: number, ticket: IProductInfo) => {
        if (ticket.forceMinPurchase) {
          return val + ticket.cost * ticket.minPurchase;
        }

        return val;
      }, 0);
      product.products.forEach((product: IProductInfo) => {
        let cost = product.cost;

        if (product.hasUserDefinedCost && product.minUserDefinedCost) {
          cost = product.minUserDefinedCost;
        }

        cost = product.forceMinPurchase ? packageForcedCost : cost * product.minPurchase + packageForcedCost;
        if (cost) {
          minList.push(cost);
          productInfoList.push(product);

          product.sessionDiscounts?.forEach((consecutiveSession: ISessionDiscount) => {
            const consecutiveSessionCost = this.getConsecutiveSessionTicketCost(cost, consecutiveSession);
            if (consecutiveSessionCost) {
              minList.push(consecutiveSessionCost);
              productInfoList.push(product);
            }
          });
        }

        if (product.pricingRule?.timeTiers && product.pricingRule?.timeRule && bookingDate) {
          const timeTier = getTicketTier(product.pricingRule.timeTiers, product.pricingRule.timeRule, bookingDate);
          priceRuleMinList.push(timeTier.price);
        }

        if (product.pricingRule?.earlyBirdBrackets && bookingDate && currentVenueTime) {
          const earlyBirdBracket = getEarlyBirdPricingRuleBracket(
            bookingDate,
            product.pricingRule.earlyBirdBrackets,
            currentVenueTime
          );

          if (earlyBirdBracket.price) {
            earlyBirdPrices.push(earlyBirdBracket.price);
          }
        }
      });
    }

    if (!minList.length) {
      return CEmptyMinProductCost;
    }

    let minCost = Math.min(...minList);
    const minIndex = minList.indexOf(minCost);

    const translationKey =
      minList.length > 1 || priceRuleMinList.length > 1 || earlyBirdPrices.length > 1
        ? 'GENERIC.FROM'
        : 'GENERIC.PRICE';
    const recurring: ICheckoutRecurringPayment | undefined = productInfoList[minIndex].recurringPayment;

    if (recurring) {
      return { minCost, translationKey, recurring };
    }

    if (priceRuleMinList.length) {
      minCost = Math.min(...priceRuleMinList);
    }

    if (earlyBirdPrices.length > 0) {
      const minEarlyBirdPrice = Math.min(...earlyBirdPrices);
      minCost = Math.min(minCost, minEarlyBirdPrice);
    }

    return { minCost, translationKey };
  }

  /**
   * Compares two sessions to determine if they are the same.
   * Should be used throughout the app when checking for session matches when possible
   * @param session first session to compare
   * @param productSession second session to compare
   * @returns true if both sessions pass all compare conditions
   */
  isSameSession(session: IProductSession | undefined, productSession: IProductSession | undefined): boolean {
    if (!session || !productSession) {
      return false;
    }

    if (!this.dateFormatService.isSameDay(session.date, productSession.date)) {
      return false;
    }

    if (session.startTime !== productSession.startTime) {
      return false;
    }

    if (session.hasOwnProperty('endTime') && productSession.hasOwnProperty('endTime')) {
      return session.endTime === productSession.endTime;
    }

    return true;
  }

  /** Converts API product type and product subtype to product type used in the cx3. Based on BE method ToApiProductType */
  convertToCxProductType(productTypeId: number, productSubTypeId?: number): TProductType {
    if (productTypeId === EProductTypes.Pass && productSubTypeId) {
      switch (productSubTypeId) {
        case this.productSubTypes.Session:
          return 'sessionpass';
        case this.productSubTypes.RecurringSessions:
          return 'recurringpass';
        default:
          return 'membership';
      }
    }

    switch (productTypeId) {
      case this.productTypes.Pass:
        return 'pass';
      case this.productTypes.AddOn:
        return 'addon';
      case this.productTypes.GiftCard:
        return 'giftcard';
      case this.productTypes.Wallet:
        return 'wallet';
      case this.productTypes.CashlessCard:
        return 'cashlesscard';
      case this.productTypes.Package:
        return 'package';
      case this.productTypes.PartyPackage:
        return 'partypackage';
      default:
        return 'custom';
    }
  }

  /**
   * Checks if a given IProduct is a forced purchase addon
   * @param product parent product to check
   * @returns true if meta indicates that it should be forced-purchased
   */
  isForcedPurchaseAddon(product: IProduct): boolean {
    if (product.type !== 'addon' || !product.meta?.length) {
      return false;
    }

    return product.meta.some(
      (meta: IProductMeta) => meta.key === 'ForcePurchaseUnlessRemoved' && meta.value === 'true'
    );
  }

  /** Finds either first exact sessionTime start, or the next available session after the suggested start time. */
  bestGuessSession(
    sessions: IProductSession[] | undefined,
    suggestedSession: TWhenAttendingProductSession | undefined,
    product: IProduct
  ): IProductSession | undefined {
    if (!sessions || !sessions.length) {
      return undefined;
    }

    const minGroupSize = Math.min(
      ...product.products.map((productInfo) => {
        if (productInfo.forceMinPurchase) {
          return productInfo.groupSize * productInfo.minPurchase;
        }

        return productInfo.groupSize;
      })
    );
    if (!suggestedSession) {
      return sessions.find((productSession) => sessionHasCapacity(productSession, product.products, minGroupSize));
    }

    return sessions?.find((productSession) => {
      if (!sessionHasCapacity(productSession, product.products, minGroupSize)) {
        return false;
      }

      if (productSession.startTime === suggestedSession.startTime && productSession.date === suggestedSession.date) {
        return true;
      }

      return (
        this.dateFormatService.hourStringToNumber(productSession.startTime) >
          this.dateFormatService.hourStringToNumber(suggestedSession.startTime) &&
        productSession.date === suggestedSession.date
      );
    });
  }

  /**
   * Gets the cost for a ticket based on a consecutive session discount
   * @param ticketCost the base cost of the ticket
   * @param sessionDiscount the consecutive session discount to apply
   * @returns the cost of the ticket modified by the number of sessions and any discount
   */
  getConsecutiveSessionTicketCost(ticketCost: number, sessionDiscount: ISessionDiscount): number {
    let costBeforeDiscount = ticketCost * sessionDiscount.numberOfSessions;
    if (sessionDiscount.amountOff) {
      costBeforeDiscount -= sessionDiscount.amountOff;
    } else if (sessionDiscount.percentOff) {
      costBeforeDiscount -= (costBeforeDiscount * sessionDiscount.percentOff) / 100;
    }

    return costBeforeDiscount;
  }

  /**
   * Filter out the following from booking edits:
   * - memberships
   * - gift cards
   * - party packages
   * - Conqueror products
   * - sometimes per-guest products
   * @param product The product to check
   * @returns true if the product can be added to an existing booking, false otherwise
   */
  canBeAddedToExistingBooking(product: IProduct): boolean {
    return !product.type || !CCannotBeAddedToExistingBooking.includes(product.type);
  }

  /**
   * Checks if a given IProduct is linked to a Conqueror web offer
   * @param product parent product to check
   * @returns true if meta indicates that it is linked to a Conqueror web offer
   */
  isLinkedToConqueror(product: IProduct): boolean {
    if (!product.meta?.length) {
      return false;
    }

    return product.meta.some(
      (meta: IProductMeta) => meta.key === 'Roller.Product.ConquerorWebOfferKey' && !!meta.value
    );
  }

  // Packages only have one session product per package, so we can safely return the first item.
  getPerGuestPackageItem(product: IProductInfo): IProductPackageItem | undefined {
    return product.packageItems?.find((packageItem) => {
      const packageItemProduct = this.productListService
        .getParentProductList()
        .find((p) => packageItem.parentProductId === p.id);

      return packageItemProduct?.durationType === 'perGuest';
    });
  }

  hasPerGuestPackageItem(parent: IProduct): boolean {
    if (parent.type !== 'package') {
      return false;
    }

    return parent.products.some((product) => this.getPerGuestPackageItem(product));
  }

  isPerGuestProductOrPackage(product: IProduct): boolean {
    if (product.type === 'sessionpass' && product.durationType === 'perGuest') {
      return true;
    }

    if (this.hasPerGuestPackageItem(product)) {
      return true;
    }

    return false;
  }
}
